import { useState, useEffect, useCallback } from 'react';
import { useRecordingTime } from './useRecordingTime';
import { useSubscription } from './useSubscription';
import { useNavigate } from 'react-router-dom';

// Define limit types
export type LimitType = 'trial_expired' | 'monthly_limit' | 'subscription_error' | 'general_limit' | null;

export const useRecordingAvailability = () => {
  const { 
    data: recordingData, 
    isLoading: isRecordingDataLoading, 
    error: recordingError,
    fetchRecordingTime,
    checkRecordingLimits
  } = useRecordingTime();

  const {
    isTrial,
    isFree,
    isPaid,
    status,
    trialEnd,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
    subscriptionDetails,
    isExpired,
    isCanceled
  } = useSubscription();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canRecord, setCanRecord] = useState<boolean>(true);
  const [limitMessage, setLimitMessage] = useState<string>('');
  const [limitType, setLimitType] = useState<LimitType>(null);
  const navigate = useNavigate();

  // Check if trial is expired
  const isTrialExpired = subscriptionDetails?.is_trial && 
    new Date(subscriptionDetails?.end_date || "") < new Date();

  // Check recording availability
  const checkAvailability = useCallback((duration: number = 0) => {
    // Reset states
    setError(null);
    setLimitMessage('');
    setCanRecord(true);
    setLimitType(null);

    // Wait until both data sources are loaded
    if (isRecordingDataLoading || isSubscriptionLoading) {
      return { canRecord: false, message: 'Checking recording availability...' };
    }

    // Check for loading errors
    if (recordingError || subscriptionError) {
      const errorMsg = 'Error checking recording availability. Please try again.';
      setError(errorMsg);
      setLimitType('subscription_error');
      return { canRecord: false, message: errorMsg };
    }

    // Check if subscription is canceled or inactive
    if (status?.toLowerCase() === 'canceled' || (status?.toLowerCase() !== 'active' && !isFree && !isTrial)) {
      const errorMsg = 'Your subscription is inactive. Please reactivate or purchase a plan to continue recording.';
      setError(errorMsg);
      setCanRecord(false);
      setLimitMessage(errorMsg);
      setLimitType('subscription_error');
      return { canRecord: false, message: errorMsg, redirectToPricing: true };
    }

    // Check if trial is expired
    if (isTrialExpired) {
      const errorMsg = 'Your trial period has expired. Please purchase a plan to continue recording.';
      setError(errorMsg);
      setCanRecord(false);
      setLimitMessage(errorMsg);
      setLimitType('trial_expired');
      return { canRecord: false, message: errorMsg, redirectToPricing: true };
    }

    // Check recording limits from useRecordingTime hook
    if (recordingData) {
      const { canRecord: canRecordBasedOnLimits, message } = checkRecordingLimits(duration);
      
      if (!canRecordBasedOnLimits) {
        setCanRecord(false);
        setLimitMessage(message);
        setLimitType('monthly_limit');
        return { canRecord: false, message, redirectToPricing: true };
      }
    }

    // If we got here, user can record
    return { canRecord: true, message: '' };
  },
    [isRecordingDataLoading, 
    isSubscriptionLoading, 
    recordingError, 
    subscriptionError, 
    status, 
    isFree, 
    isTrial, 
    isTrialExpired, 
    recordingData, 
    checkRecordingLimits]
  );

  // Check availability on mount and when dependencies change
  useEffect(() => {
    const result = checkAvailability();
    setCanRecord(result.canRecord);
    setLimitMessage(result.message);
    setIsLoading(isRecordingDataLoading || isSubscriptionLoading);
  }, [
    checkAvailability,
    isRecordingDataLoading,
    isSubscriptionLoading
  ]);

  // Function to handle redirecting to pricing if needed
  const handleLimitReached = () => {
    navigate('/pricing');
  };

  // Refresh both data sources
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    if (fetchRecordingTime) {
      await fetchRecordingTime();
    }
    setIsLoading(false);
    return checkAvailability();
  }, [fetchRecordingTime, checkAvailability]);

  return {
    isLoading,
    error,
    canRecord,
    limitMessage,
    limitType,
    checkAvailability,
    handleLimitReached,
    refreshData,
    recordingData,
    subscriptionDetails,
    isTrial,
    isFree,
    isPaid,
    status
  };
}; 